import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { DeliveryView, DevListingView, ReviewView, VersionView } from '@dunea/shared';
import { apiGetChats } from '~/lib/api/chats';
import {
  apiCreateVersion,
  apiDevInstall,
  apiGetListingAnalytics,
  apiGetReviewStatus,
  apiListDeliveries,
  apiListListingReviews,
  apiReleaseVersion,
  apiReplyToMarketplaceReview,
  apiRotateSecret,
  apiSendTestWebhook,
  apiSubmitVersion,
} from '~/lib/api/marketplace';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CAPTION,
  CARD,
  ErrorBanner,
  Field,
  LoadError,
  Pill,
  Segmented,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { formatDate } from '~/components/workbench/tools/marketing/marketing-model';
import { ListingBadges, ListingIcon, Stars } from '~/components/workbench/tools/marketplace/parts';
import { cardPriceLabel, creditsLabel, formatUsd } from '~/components/workbench/tools/marketplace/marketplace-model';
import {
  analyticsBars,
  apiErrorMessage,
  canReleaseVersion,
  canSubmitVersion,
  churnRows,
  percent,
  reviewModeLabel,
  reviewStatusLine,
  sourceRows,
  checkManifestText,
  DELIVERY_FILTERS,
  deliveryStatusMeta,
  headlineVersion,
  listingStatusMeta,
  sortVersions,
  versionManifestErrors,
  versionStatusMeta,
  type ManifestCheck,
} from '~/components/marketplace/developer-model';
import { ServerCheck } from './ServerCheck';
import { ConfirmButton, ManifestInput, SecretReveal } from './shared';

/** The developer's listings; each row expands into versions, deliveries, reviews and its secret. */
export function ListingsSection({
  listings,
  onChanged,
}: {
  listings: DevListingView[];
  onChanged: (next: DevListingView) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);

  if (listings.length === 0) {
    return (
      <div className={classNames(CARD, 'px-4 py-8 text-center text-[12.5px] text-muted')}>
        No listings yet. Publish one from the CLI, or paste a manifest with "New listing".
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {listings.map((l) => (
        <ListingRow
          key={l.slug}
          listing={l}
          open={open === l.slug}
          onToggle={() => setOpen((cur) => (cur === l.slug ? null : l.slug))}
          onChanged={onChanged}
        />
      ))}
    </div>
  );
}

type Tab = 'versions' | 'analytics' | 'test' | 'deliveries' | 'reviews' | 'secret';

function ListingRow({
  listing,
  open,
  onToggle,
  onChanged,
}: {
  listing: DevListingView;
  open: boolean;
  onToggle: () => void;
  onChanged: (next: DevListingView) => void;
}) {
  const [tab, setTab] = useState<Tab>('versions');
  const status = listingStatusMeta(listing.status);
  const head = headlineVersion(listing.versions);
  const headMeta = head ? versionStatusMeta(head.status) : null;
  const isPlugin = listing.kind === 'plugin';
  const installable = listing.kind !== 'template';
  const tabs: (readonly [Tab, string])[] = [
    ['versions', 'Versions'],
    ['analytics', 'Analytics'],
    ...(installable ? ([['test', 'Test in my app']] as const) : []),
    ...(isPlugin ? ([['deliveries', 'Deliveries']] as const) : []),
    ['reviews', 'Reviews'],
    ...(isPlugin ? ([['secret', 'Client secret']] as const) : []),
  ];

  const setVersions = (versions: VersionView[]) => onChanged({ ...listing, versions });

  return (
    <div className={classNames(CARD, 'overflow-hidden')}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-left transition-colors hover:bg-white/[0.02]"
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <ListingIcon url={listing.iconUrl} name={listing.name} size={32} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate text-[13px] font-medium text-primary">{listing.name}</span>
              <span className="text-[10.5px] uppercase tracking-[0.08em] text-muted">{listing.kind}</span>
            </div>
            <div className="text-[11px] text-muted">
              <code className="font-geist-mono">{listing.slug}</code> · {cardPriceLabel(listing)}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2.5 text-[11px] text-muted">
          <ListingBadges item={listing} />
          <Pill tone={status.tone} label={status.label} />
          {head && headMeta && <Pill tone={headMeta.tone} label={`v${head.version} ${headMeta.label}`} />}
          <span className="[font-variant-numeric:tabular-nums]">{listing.activeInstalls} active</span>
          <span className="inline-flex items-center gap-1">
            <Stars rating={listing.rating} />
            {listing.ratingCount}
          </span>
          <div className={classNames(open ? 'i-ph:caret-up' : 'i-ph:caret-down', 'text-[13px]')} />
        </div>
      </button>

      {open && (
        <div className="space-y-4 border-t border-hairline p-4">
          <Segmented value={tab} options={tabs} onChange={setTab} label={`${listing.name} sections`} />
          {tab === 'versions' && <VersionsPanel listing={listing} onVersions={setVersions} />}
          {tab === 'analytics' && <AnalyticsPanel slug={listing.slug} kind={listing.kind} />}
          {tab === 'test' && installable && <DevInstallPanel slug={listing.slug} />}
          {tab === 'deliveries' && isPlugin && <DeliveriesPanel slug={listing.slug} />}
          {tab === 'reviews' && <ReviewsReplyPanel slug={listing.slug} />}
          {tab === 'secret' && isPlugin && <SecretPanel slug={listing.slug} />}
        </div>
      )}
    </div>
  );
}

// ─── versions ───

function VersionsPanel({
  listing,
  onVersions,
}: {
  listing: DevListingView;
  onVersions: (versions: VersionView[]) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [releasing, setReleasing] = useState<string | null>(null);
  const versions = sortVersions(listing.versions);
  const statusKey = listing.versions.map((v) => `${v.id}:${v.status}`).join(',');
  const review = useResource(() => apiGetReviewStatus(listing.slug), `${listing.slug}|${statusKey}`);
  const reviewLine = reviewStatusLine(review.data);

  const submit = async (v: VersionView) => {
    setSubmitting(v.id);

    try {
      const next = await apiSubmitVersion(listing.slug, v.version);
      onVersions(listing.versions.map((x) => (x.id === next.id ? next : x)));
      toast.success(
        next.status === 'approved'
          ? `v${v.version} was approved automatically.`
          : `v${v.version} submitted for review.`,
      );
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not submit this version.'));
    } finally {
      setSubmitting(null);
    }
  };

  const release = async (v: VersionView) => {
    setReleasing(v.id);

    try {
      const next = await apiReleaseVersion(listing.slug, v.version);
      onVersions(
        listing.versions.map((x) =>
          x.id === next.id ? next : x.status === 'approved' && x.current ? { ...x, current: false } : x,
        ),
      );
      toast.success(`v${v.version} is now the version new installs get.`);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not release this version.'));
    } finally {
      setReleasing(null);
    }
  };

  return (
    <div className="space-y-3">
      {reviewLine && (
        <div className="flex items-center gap-2 rounded-lg border border-info/25 bg-info/[0.05] px-3 py-2 text-[12px] text-info">
          <div className="i-ph:hourglass-medium shrink-0 text-[13px]" />
          {reviewLine}
        </div>
      )}
      {uploading ? (
        <NewVersionForm
          listing={listing}
          onCancel={() => setUploading(false)}
          onCreated={(v) => {
            onVersions([v, ...listing.versions.filter((x) => x.id !== v.id)]);
            setUploading(false);
          }}
        />
      ) : (
        <button type="button" onClick={() => setUploading(true)} className={BTN_SECONDARY}>
          <div className="i-ph:upload-simple text-[13px]" />
          Upload new version
        </button>
      )}

      {versions.length === 0 ? (
        <p className="text-[12px] text-muted">No versions yet.</p>
      ) : (
        <ul className="space-y-2">
          {versions.map((v) => (
            <VersionItem
              key={v.id}
              version={v}
              submitting={submitting === v.id}
              onSubmit={() => void submit(v)}
              releasing={releasing === v.id}
              onRelease={() => void release(v)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function VersionItem({
  version,
  submitting,
  onSubmit,
  releasing,
  onRelease,
}: {
  version: VersionView;
  submitting: boolean;
  onSubmit: () => void;
  releasing: boolean;
  onRelease: () => void;
}) {
  const [showChecks, setShowChecks] = useState(version.status === 'draft' || version.status === 'rejected');
  const meta = versionStatusMeta(version.status);
  const failing = version.checks.filter((c) => !c.ok).length;
  const mode = version.status === 'approved' ? reviewModeLabel(version.reviewMode) : null;

  return (
    <li className="space-y-2 rounded-lg border border-hairline bg-sunken p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-geist-mono text-[12px] text-primary">v{version.version}</span>
        <Pill tone={meta.tone} label={meta.label} />
        {version.status === 'approved' && version.current && <Pill tone="accent" label="Current" />}
        {mode && <span className="text-[10.5px] uppercase tracking-[0.08em] text-muted">{mode}</span>}
        <span className="text-[11px] text-muted">
          {version.submittedAt ? `Submitted ${formatDate(version.submittedAt)}` : 'Not submitted'}
          {version.reviewedAt && ` · reviewed ${formatDate(version.reviewedAt)}`}
        </span>
        <div className="flex-1" />
        {version.checks.length > 0 && (
          <button type="button" onClick={() => setShowChecks((s) => !s)} className={BTN_GHOST}>
            {failing ? `${failing} failing ${failing === 1 ? 'check' : 'checks'}` : 'Checks passed'}
          </button>
        )}
        {canSubmitVersion(version) && (
          <button type="button" onClick={onSubmit} disabled={submitting} className={BTN_SECONDARY}>
            {submitting && <Spinner />}
            Submit for review
          </button>
        )}
        {canReleaseVersion(version) && (
          <ConfirmButton
            label="Release this version"
            icon="i-ph:arrow-counter-clockwise"
            question={`Make v${version.version} the version new installs get?`}
            confirmLabel={releasing ? 'Releasing…' : 'Release'}
            busy={releasing}
            onConfirm={onRelease}
          />
        )}
      </div>
      {version.status === 'rejected' && version.rejectionCodes.length > 0 && (
        <ul className="space-y-1 rounded-md border border-danger/30 bg-danger/[0.05] px-3 py-2">
          {version.rejectionCodes.map((c) => (
            <li key={c.id} className="text-[12px] leading-relaxed text-danger/90">
              <code className="mr-1.5 font-geist-mono text-[10.5px] text-danger">{c.id}</code>
              {c.rule}
            </li>
          ))}
        </ul>
      )}
      {version.reviewNotes && (
        <div
          className={classNames(
            'rounded-md border px-3 py-2 text-[12px] leading-relaxed',
            version.status === 'rejected'
              ? 'border-danger/30 bg-danger/[0.05] text-danger/90'
              : 'border-hairline text-secondary',
          )}
        >
          <span className="mr-1 text-[10px] uppercase tracking-[0.1em] text-muted">Review notes</span>
          <span className="whitespace-pre-line">{version.reviewNotes}</span>
        </div>
      )}
      {version.changelog && (
        <p className="whitespace-pre-line text-[11.5px] leading-relaxed text-muted">{version.changelog}</p>
      )}
      {showChecks && version.checks.length > 0 && (
        <ul className="space-y-1">
          {version.checks.map((c) => (
            <li key={c.name} className="flex items-start gap-2 text-[11.5px] leading-relaxed">
              <span
                className={classNames(
                  'mt-0.5 shrink-0 text-[12px]',
                  c.ok ? 'i-ph:check-circle text-success' : 'i-ph:x-circle text-danger',
                )}
              />
              <span className="text-secondary">
                <span className="font-geist-mono text-[10.5px] text-primary">{c.name}</span> {c.message}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function NewVersionForm({
  listing,
  onCreated,
  onCancel,
}: {
  listing: DevListingView;
  onCreated: (version: VersionView) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState('');
  const [check, setCheck] = useState<ManifestCheck>(() => checkManifestText(''));
  const [changelog, setChangelog] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const extra = versionManifestErrors(check.manifest, listing);
  const ready = check.ok && extra.length === 0;

  const submit = async () => {
    if (!ready) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const v = await apiCreateVersion(listing.slug, check.raw, changelog);
      toast.success(`v${v.version} uploaded as a draft.`);
      onCreated(v);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not upload this version.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="space-y-3 rounded-lg border border-hairline p-3"
    >
      <h4 className={CAPTION}>New version of {listing.slug}</h4>
      <ManifestInput
        value={text}
        onChange={(next, nextCheck) => {
          setText(next);
          setCheck(nextCheck);
        }}
        extraErrors={extra}
        disabled={busy}
      />
      <ServerCheck raw={check.raw} disabled={busy} />
      <Field label="Changelog (optional)">
        <textarea
          value={changelog}
          onChange={(e) => setChangelog(e.target.value)}
          rows={3}
          className="ui-input resize-y"
        />
      </Field>
      {error && <ErrorBanner message={error} />}
      <div className="flex gap-2">
        <button type="submit" disabled={busy || !ready} className={BTN_SECONDARY}>
          {busy && <Spinner />}
          Upload version
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={BTN_GHOST}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── analytics ───

const WINDOWS = [
  ['7', '7 days'],
  ['30', '30 days'],
  ['90', '90 days'],
] as const;

function AnalyticsPanel({ slug, kind }: { slug: string; kind: DevListingView['kind'] }) {
  const [days, setDays] = useState<'7' | '30' | '90'>('30');
  const analytics = useResource(() => apiGetListingAnalytics(slug, Number(days)), `${slug}:${days}`);
  const a = analytics.data;

  return (
    <div className="space-y-4">
      <Segmented value={days} options={WINDOWS} onChange={setDays} label="Window" />
      {analytics.error ? (
        <LoadError message="Could not load analytics." onRetry={() => void analytics.reload()} />
      ) : !a ? (
        <SkeletonCard lines={3} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Metric label="Views" value={a.totals.views.toLocaleString('en-US')} />
            <Metric
              label={kind === 'template' ? 'Remixes' : 'Installs'}
              value={(kind === 'template' ? a.totals.remixes : a.totals.installs).toLocaleString('en-US')}
            />
            <Metric label="Conversion" value={percent(a.conversion)} />
            <Metric label="Net revenue" value={formatUsd(a.totals.netRevenue)} />
            {kind !== 'template' && (
              <>
                <Metric label="Active installs" value={a.totals.activeInstalls.toLocaleString('en-US')} />
                <Metric label="Uninstalls" value={a.totals.uninstalls.toLocaleString('en-US')} />
                <Metric label="Install clicks" value={a.totals.installClicks.toLocaleString('en-US')} />
              </>
            )}
            {kind === 'plugin' && <Metric label="Usage" value={creditsLabel(a.totals.usageCredits)} />}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <BarChart title="Views per day" bars={analyticsBars(a.daily, 'views')} />
            <BarChart title="Installs per day" bars={analyticsBars(a.daily, 'installs')} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {kind !== 'template' && (
              <div className="space-y-1.5">
                <h4 className={CAPTION}>Uninstalls, by time since install</h4>
                <ul className="space-y-1.5">
                  {churnRows(a.churn).map((r) => (
                    <li key={r.label} className="space-y-0.5">
                      <div className="flex justify-between text-[11.5px]">
                        <span className="text-secondary">{r.label}</span>
                        <span className="text-muted [font-variant-numeric:tabular-nums]">
                          {r.count} · {percent(r.share, 0)}
                        </span>
                      </div>
                      <div className="h-1 overflow-hidden rounded-full bg-sunken">
                        <div className="h-full rounded-full bg-warning/70" style={{ width: `${r.share * 100}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="space-y-1.5">
              <h4 className={CAPTION}>Sources</h4>
              {a.sources.length === 0 ? (
                <p className="text-[12px] text-muted">No views yet.</p>
              ) : (
                <ul className="space-y-1.5">
                  {sourceRows(a.sources).map((r) => (
                    <li key={r.source} className="space-y-0.5">
                      <div className="flex justify-between gap-2 text-[11.5px]">
                        <span className="truncate text-secondary">{r.source}</span>
                        <span className="shrink-0 text-muted [font-variant-numeric:tabular-nums]">
                          {r.views} views · {r.installs} installs · {percent(r.conversion)}
                        </span>
                      </div>
                      <div className="h-1 overflow-hidden rounded-full bg-sunken">
                        <div className="h-full rounded-full bg-info/70" style={{ width: `${r.width * 100}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-lg border border-hairline bg-sunken px-3 py-2">
      <div className="truncate text-[10.5px] text-muted">{label}</div>
      <div className="mt-0.5 text-[15px] leading-none text-primary [font-variant-numeric:tabular-nums]">{value}</div>
    </div>
  );
}

function BarChart({ title, bars }: { title: string; bars: ReturnType<typeof analyticsBars> }) {
  return (
    <div className="space-y-1.5">
      <h4 className={CAPTION}>{title}</h4>
      <div className="flex h-16 items-end gap-px rounded-lg border border-hairline bg-sunken px-1.5 pt-1.5" aria-hidden>
        {bars.map((b) => (
          <div
            key={b.date}
            title={`${b.date}: ${b.value}`}
            className={classNames('min-w-0 flex-1 rounded-t-[1px]', b.value > 0 ? 'bg-accent/70' : 'bg-white/[0.04]')}
            style={{ height: `${Math.max(4, b.height * 100)}%` }}
          />
        ))}
      </div>
      {bars.length > 0 && (
        <div className="flex justify-between text-[10px] text-muted">
          <span>{formatDate(bars[0].date)}</span>
          <span>{formatDate(bars[bars.length - 1].date)}</span>
        </div>
      )}
    </div>
  );
}

// ─── dev install ───

function DevInstallPanel({ slug }: { slug: string }) {
  const chats = useResource(() => apiGetChats(), 'my-chats');
  const [appId, setAppId] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ appId: string; version: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const install = async () => {
    if (!appId) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const result = await apiDevInstall(slug, appId);
      setDone({ appId, version: result.version });
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not install on that app.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-[12px] leading-relaxed text-secondary">
        Installs your newest version on one of your apps before review. Only you see it, and it's never billed.
      </p>
      {chats.error ? (
        <LoadError message="Could not load your apps." onRetry={() => void chats.reload()} />
      ) : !chats.data ? (
        <SkeletonCard lines={1} />
      ) : chats.data.length === 0 ? (
        <p className="text-[12px] text-muted">You have no apps yet. Start one from the home page.</p>
      ) : (
        <div className="flex flex-col gap-2 sm:flex-row">
          <select
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            disabled={busy}
            aria-label="App"
            className="ui-input !py-2"
          >
            <option value="">Pick an app</option>
            {chats.data.map((c) => (
              <option key={c.id} value={c.id}>
                {c.description || 'Untitled app'}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void install()}
            disabled={busy || !appId}
            className={classNames(BTN_SECONDARY, 'shrink-0')}
          >
            {busy ? <Spinner /> : <div className="i-ph:flask text-[13px]" />}
            Install for testing
          </button>
        </div>
      )}
      {error && <ErrorBanner message={error} />}
      {done && (
        <p className="flex flex-wrap items-center gap-1.5 text-[12px] text-success">
          <span className="i-ph:check-circle text-[13px]" />v{done.version} installed on the dev channel.
          <Link
            to={`/chat/${encodeURIComponent(done.appId)}`}
            className="text-secondary underline decoration-subtle underline-offset-2 hover:text-primary"
          >
            Open the app
          </Link>
        </p>
      )}
    </div>
  );
}

// ─── deliveries ───

function DeliveriesPanel({ slug }: { slug: string }) {
  const [filter, setFilter] = useState<'' | DeliveryView['status']>('');
  const deliveries = useResource(() => apiListDeliveries(slug, filter || undefined), `${slug}:${filter}`);
  const [sending, setSending] = useState(false);

  const test = async () => {
    setSending(true);

    try {
      const d = await apiSendTestWebhook(slug);
      deliveries.setData((prev) => [d, ...(prev ?? [])]);

      const meta = deliveryStatusMeta(d.status);
      toast.info(`Test webhook: ${meta.label}${d.lastStatusCode ? ` (${d.lastStatusCode})` : ''}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not send a test webhook.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Segmented
          value={filter}
          options={DELIVERY_FILTERS}
          onChange={(v) => setFilter(v as typeof filter)}
          label="Delivery status"
        />
        <button type="button" onClick={() => void test()} disabled={sending} className={BTN_SECONDARY}>
          {sending ? <Spinner /> : <div className="i-ph:paper-plane-tilt text-[13px]" />}
          Send test webhook
        </button>
      </div>

      {deliveries.error ? (
        <LoadError message="Could not load deliveries." onRetry={() => void deliveries.reload()} />
      ) : !deliveries.data ? (
        <SkeletonCard />
      ) : deliveries.data.length === 0 ? (
        <p className="text-[12px] text-muted">No deliveries{filter ? ' with this status' : ' yet'}.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-hairline">
          <table className="w-full min-w-[560px] text-left text-[11.5px]">
            <thead className="border-b border-hairline text-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Event</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Attempts</th>
                <th className="px-3 py-2 font-medium">Last error</th>
                <th className="px-3 py-2 font-medium">Sent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {deliveries.data.map((d) => {
                const meta = deliveryStatusMeta(d.status);

                return (
                  <tr key={d.id} className="align-top">
                    <td className="px-3 py-2 font-geist-mono text-[10.5px] text-primary">{d.event}</td>
                    <td className="px-3 py-2">
                      <Pill tone={meta.tone} label={meta.label} />
                    </td>
                    <td className="px-3 py-2 text-secondary [font-variant-numeric:tabular-nums]">{d.attempts}</td>
                    <td className="max-w-[240px] px-3 py-2 text-secondary">
                      {d.lastStatusCode ? <span className="font-geist-mono">{d.lastStatusCode} </span> : null}
                      <span className="break-words">{d.lastError ?? (d.lastStatusCode ? '' : '—')}</span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted">{formatDate(d.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── reviews ───

function ReviewsReplyPanel({ slug }: { slug: string }) {
  const reviews = useResource(() => apiListListingReviews(slug), slug);

  if (reviews.error) {
    return <LoadError message="Could not load reviews." onRetry={() => void reviews.reload()} />;
  }

  if (!reviews.data) {
    return <SkeletonCard />;
  }

  if (reviews.data.items.length === 0) {
    return <p className="text-[12px] text-muted">No reviews yet.</p>;
  }

  return (
    <ul className="space-y-2">
      {reviews.data.items.map((r) => (
        <ReviewReplyItem
          key={r.id}
          review={r}
          onReplied={(reply) =>
            reviews.setData((prev) =>
              prev
                ? { ...prev, items: prev.items.map((x) => (x.id === r.id ? { ...x, developerReply: reply } : x)) }
                : prev!,
            )
          }
        />
      ))}
    </ul>
  );
}

function ReviewReplyItem({ review, onReplied }: { review: ReviewView; onReplied: (reply: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [reply, setReply] = useState(review.developerReply ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!reply.trim()) {
      setError('Write a reply first.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      await apiReplyToMarketplaceReview(review.id, reply.trim());
      onReplied(reply.trim());
      setEditing(false);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not post your reply.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="space-y-1.5 rounded-lg border border-hairline bg-sunken p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 text-[11px] text-muted">
          <Stars rating={review.rating} />
          {review.authorName} · {formatDate(review.createdAt)}
        </span>
        {!editing && (
          <button type="button" onClick={() => setEditing(true)} className={BTN_GHOST}>
            {review.developerReply ? 'Edit reply' : 'Reply'}
          </button>
        )}
      </div>
      {review.title && <div className="text-[12.5px] font-medium text-primary">{review.title}</div>}
      {review.body && <p className="whitespace-pre-line text-[12px] leading-relaxed text-secondary">{review.body}</p>}
      {review.developerReply && !editing && (
        <p className="border-l-2 border-subtle pl-2.5 text-[12px] leading-relaxed text-secondary">
          {review.developerReply}
        </p>
      )}
      {editing && (
        <div className="space-y-2">
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={3}
            maxLength={2000}
            aria-label="Your reply"
            className="ui-input resize-y"
          />
          {error && <ErrorBanner message={error} />}
          <div className="flex gap-2">
            <button type="button" onClick={() => void save()} disabled={busy} className={BTN_SECONDARY}>
              {busy && <Spinner />}
              Post reply
            </button>
            <button type="button" onClick={() => setEditing(false)} disabled={busy} className={BTN_GHOST}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

// ─── secret ───

function SecretPanel({ slug }: { slug: string }) {
  const [busy, setBusy] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);

  const rotate = async () => {
    setBusy(true);

    try {
      const { clientSecret } = await apiRotateSecret(slug);
      setSecret(clientSecret);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not rotate the secret.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-[12px] leading-relaxed text-secondary">
        Your service verifies Dunea's webhooks and tool calls with this secret. Rotating it issues a new one; pass both
        to the SDK's verifier while you deploy it.
      </p>
      {secret ? (
        <SecretReveal
          label="DUNEA_CLIENT_SECRET"
          value={secret}
          note="Update your service's secret now."
          onDone={() => setSecret(null)}
        />
      ) : (
        <ConfirmButton
          label="Rotate secret"
          icon="i-ph:arrows-clockwise"
          question="Issue a new client secret?"
          confirmLabel={busy ? 'Rotating…' : 'Rotate'}
          busy={busy}
          onConfirm={() => void rotate()}
        />
      )}
    </div>
  );
}
